import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import dbMapUrl from '../assets/maps/db-map.svg?url';
import dbOverviewUrl from '../assets/maps/db-overview.svg?url';
import portalMapUrl from '../assets/maps/portal-map.svg?url';
import { Button } from '../ui/button';
import { Page, PageHeader, Panel } from '../ui/layout';

const MAPS = [
  {
    id: 'portal',
    title: 'Разделы портала и связи между ними',
    text: 'Разделы по кольцу: «Компания», «Проект» по ходу работ, «Система». Зелёная линия — связь работает (внешние ключи базы и логика), оранжевый пунктир — своя сторона сделана и ждёт другой поток, серые точки — по ТЗ нужно, ещё не начато.',
    url: portalMapUrl,
  },
  {
    id: 'db-overview',
    title: 'База данных: схемы и таблицы',
    text: 'Каждая схема базы — отдельный раздел работы: таблицы внутри, стрелки — сколько внешних ключей ведёт из одной схемы в другую.',
    url: dbOverviewUrl,
  },
  {
    id: 'db',
    title: 'База данных: все таблицы и внешние ключи',
    text: 'Полная карта: синие стрелки — связи между схемами, серые — внутри схемы. Удобно открыть во всю ширину и увеличить.',
    url: dbMapUrl,
  },
] as const;

/**
 * Карта связей базы и портала: строится скриптом `node scripts/links-map.mjs` из схем базы, реестра разделов и
 * docs/links.md при каждой выкладке, которая меняет таблицы или связи (правило «Связи между разделами» в CLAUDE.md).
 */
export function LinksMapPage() {
  const [zoom, setZoom] = useState<Record<string, number>>({});
  const scale = (id: string) => zoom[id] ?? 1;
  const setScale = (id: string, v: number) => setZoom((z) => ({ ...z, [id]: Math.min(4, Math.max(0.5, v)) }));
  return (
    <Page>
      <PageHeader
        title="Карта связей"
        subtitle="Как разделы портала и таблицы базы связаны между собой. Карта строится из кода при каждой выкладке; состояние связей — в docs/links.md репозитория."
        actions={
          <Link
            to="/roadmap"
            className="inline-flex h-11 items-center rounded-md border border-line-strong px-4 text-[14px] whitespace-nowrap text-ink hover:bg-tile/70 md:h-10"
          >
            К ходу разработки
          </Link>
        }
      />
      <div className="flex flex-col gap-6">
        {MAPS.map((m) => (
          <Panel
            key={m.id}
            title={m.title}
            meta={m.text}
            actions={
              <>
                <Button size="sm" onClick={() => setScale(m.id, scale(m.id) - 0.25)} aria-label="Уменьшить">
                  −
                </Button>
                <span className="num text-[13px] text-ink-2">{Math.round(scale(m.id) * 100)} %</span>
                <Button size="sm" onClick={() => setScale(m.id, scale(m.id) + 0.25)} aria-label="Увеличить">
                  +
                </Button>
                <a
                  href={m.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex h-11 items-center rounded-md px-3 text-[13px] text-ink-2 hover:text-ink md:h-8"
                >
                  Открыть отдельно
                </a>
              </>
            }
            bodyClassName="overflow-auto p-3"
          >
            <img
              src={m.url}
              alt={m.title}
              className="max-w-none"
              style={{ width: `${scale(m.id) * 100}%` }}
              loading="lazy"
            />
          </Panel>
        ))}
      </div>
    </Page>
  );
}
